import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PackService } from '../document-review/services/pack.service';
import { ForceAny } from '../../../shared/typescript';
import { AuthService } from '../../auth/services/auth.service';
import { DocumentChecklistConfig } from '@shared/components';
import PDFObject from 'pdfobject';
import { getFormateDate } from '@shared/utils';
import { TooltipModule } from 'primeng/tooltip';
import { NgTemplateOutlet } from '@angular/common';
import { TRANSLATION_CATEGORIES } from '@entities/constants';
import { TranslationPipe } from '@shared/pipes';
import { LinkDetails } from '@shared/types';
import { ToastService } from '../../../shared/services';
import { TOKENS } from '@core/types';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-view-shared-pack',
  standalone: true,
  imports: [TooltipModule, NgTemplateOutlet, TranslationPipe],
  templateUrl: './view-shared-pack.component.html',
  styleUrl: './view-shared-pack.component.scss',
  providers: [AuthService, PackService],
})
export class ViewSharedPackComponent {
  sharedPackDetails!: LinkDetails;
  documentChecklist!: DocumentChecklistConfig[];

  readonly textTranslation = TRANSLATION_CATEGORIES.PAGE_TEXT;

  $subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private packService: PackService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.retrieveRouteParams();
  }

  retrieveRouteParams() {
    this.route.paramMap.subscribe((params) => {
      const encodedState = params.get('token');
      const accessToken = params.get('accessToken');
      if (encodedState) {
        if (accessToken) this.storeAccessToken(accessToken);
        this.getSharedPackDetails(encodedState);
      }
    });
  }

  private storeAccessToken(accessToken: string): void {
    const token = decodeURIComponent(accessToken);

    localStorage.setItem(TOKENS.accessToken, token);
    document.cookie = `x-access-token=${encodeURIComponent(
      token,
    )}; Path=/; Secure; SameSite=None; Max-Age=86400`;
  }

  getSharedPackDetails(token: string) {
    this.$subscription.add(
      this.packService.getPackDetailsByToken(token).subscribe({
        next: (res) => {
          this.sharedPackDetails = res.data;
          this.initPDFObject();
          if (this.sharedPackDetails.docTypes.length) this.getDocTypeList();
        },
        error: (err) => {
          if (err?.status === 401) {
            const redirectUrl = `${window.location.origin}/packs/share/${token}`;
            window.location.href = `${environment.drm_url}/auth/login?redirectUrl=${encodeURIComponent(
              redirectUrl,
            )}`;
            return;
          }
          this.toastService.quickToast(err?.error?.message ?? err?.message);
        },
      }),
    );
  }

  getDocTypeList() {
    this.documentChecklist =
      this.sharedPackDetails.docTypes.map((item: ForceAny) => {
        const docTypeId = item?.id;
        const date = item.packMappings?.[0]?.created_at;
        const documentId = item.packMappings?.[0]?.documentId;
        const matchedFileName =
          item.packMappings?.[0]?.document.originalName || '';
        return {
          ...item,
          title: item.name || '',
          fileName: matchedFileName,
          status: item.packMappings?.length ? 'received' : 'missing',
          documentId,
          docTypeId,
          showStatusDot: true,
          showFileName: true,
          conditional: false,
          date: getFormateDate(date) || '',
        };
      }) || [];
  }

  initPDFObject() {
    const url = this.sharedPackDetails.signedUrls.mainFile || '';
    PDFObject.embed(`${url}#toolbar=0`, '#pdf-container', {
      suppressConsole: false,
      PDFJS_URL: url,
      height: '550px',
    });
  }

  downloadPack(id: string) {
    this.$subscription.add(
      this.packService.downloadSharedPack(id).subscribe({
        next: (res) => {
          if (res.data) {
            this.openPdfInNewTab(res.data.signedUrls.mainFile);
          }
        },
      }),
    );
  }

  openPdfInNewTab(pdfUrl: string): void {
    const newTab = window.open(pdfUrl, '_blank');
    if (!newTab) {
      alert('Popup blocked. Please allow popups for this site.');
    }
  }

  ngOnDestroy() {
    this.$subscription.unsubscribe();
  }
}
